/**
 * Auth store — 登入狀態 + 用戶資訊
 *
 * Cookie 在 BFF 那邊（HttpOnly），前端無法直接讀。只能透過 GET /auth/me
 * 詢問 BFF「我登入了嗎、是誰？」
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { UserInfo } from '@/types/api.types';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<UserInfo | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  const isAuthenticated = computed(() => user.value !== null);
  const isSuperAdmin = computed(() => user.value?.role === 'super_admin');
  const isImpersonating = computed(() => !!user.value?.impersonator);

  // In-flight 保護：多個 caller（router guard + App.vue onMounted）會同時 call fetchMe。
  // 沒有這個保護的話，第一個 call 把 loading 設 true，第二個 caller 看 loading=true 就 skip，
  // 結果第二個 caller 立刻判斷 user=null → 跳 /login（race condition）。
  // 加 _inFlight 後，所有 caller 共享同一個 promise，await 同一個結果。
  let _inFlight: Promise<void> | null = null;

  async function fetchMe(): Promise<void> {
    if (_inFlight) return _inFlight;
    _inFlight = (async () => {
      loading.value = true;
      error.value = null;
      try {
        const res = await fetch('/auth/me', { credentials: 'include' });
        if (res.status === 401) {
          user.value = null;
          return;
        }
        if (!res.ok) {
          error.value = `auth_check_failed_${res.status}`;
          return;
        }
        user.value = (await res.json()) as UserInfo;
      } catch (e) {
        error.value = (e as Error).message;
      } finally {
        loading.value = false;
        _inFlight = null;
      }
    })();
    return _inFlight;
  }

  /** 觸發 OAuth flow（會 redirect 整頁到 BFF） */
  function login(): void {
    window.location.href = '/auth/login';
  }

  /** 登出：呼叫 BFF logout，清狀態 */
  async function logout(): Promise<void> {
    try {
      await fetch('/auth/logout', { method: 'POST', credentials: 'include' });
    } catch {
      // best-effort
    }
    user.value = null;
    window.location.href = '/';
  }

  /** 內部：被 401 攔截觸發，清狀態但不打 logout */
  function clear(): void {
    user.value = null;
  }

  return { user, loading, error, isAuthenticated, isSuperAdmin, isImpersonating, fetchMe, login, logout, clear };
});
